import { useContext, useEffect, useMemo } from 'react'

import { Box, Button, Icon } from '@fairfashion/design-system'
import { Handle, Position } from 'reactflow'

import { SegmentsDataContext } from '../../../../contexts/segments'
import { OriginItemsContext } from '../../../../pages/OriginItems/origin-items.context'
import { OriginItem } from '../../../../pages/OriginItems/origin-items.types'
import { docMask } from '../../../../utilities/masks/brasilianDocuments'

import * as Styles from './next-item-node.styles'

export function NextItemNode({
  data,
  sourcePosition,
  allowEdditing = true,
}: {
  data: OriginItem
  sourcePosition?: any
  allowEdditing?: boolean
}) {
  const { direction, actions, allowMultipleLevels } =
    useContext(OriginItemsContext)
  const { segments } = useContext(SegmentsDataContext)

  const segment = segments.find(
    (item) => item.name === data.itemProviderRelation?.companyProviderSegment,
  )

  useEffect(() => {
    actions.setDirection(
      sourcePosition === 'right' || sourcePosition === 'left' ? 'LR' : 'TB',
    )
  }, [sourcePosition])

  const allowNextLevel = useMemo(() => {
    if (allowMultipleLevels) return true

    if (data.level === 0) return true

    return false
  }, [allowMultipleLevels, data])

  return (
    <>
      {/* Handle para entrada de conexão */}
      <Handle
        type="target"
        position={direction === 'LR' ? Position.Left : Position.Top}
        isConnectableStart={false}
        style={{ opacity: 0, visibility: 'hidden' }}
      />
      <Styles.Node>
        <Box width="100%" flex={1} direction="column" gap={2}>
          <Box width="100%" flex={1} justifyContent="space-between">
            <Styles.SegmentText color={segment?.color ?? 'transparent'}>
              <span></span>
              {segment?.translate}
            </Styles.SegmentText>
            <Box alignItems="center">
              {allowEdditing && (
                <Button
                  variant="text"
                  padding="0"
                  onClick={() => {
                    actions.handleNodeClick(data, true)
                  }}
                >
                  <Icon name="pencil" color="info" />
                </Button>
              )}
            </Box>
          </Box>
          <Box flex={1} width="100%" direction="column">
            <Styles.SmallText>
              {data.itemProviderRelation?.companyProviderName}
            </Styles.SmallText>
            <Styles.SmallText color="grey_3">
              {docMask(data.itemProviderRelation?.companyProviderTaxId ?? '')}
            </Styles.SmallText>
          </Box>
        </Box>
        <Box flex={1} width="100%" direction="column" gap={2}>
          <Box width="100%" direction="column">
            <Styles.BoldText>{data.description ?? ''}</Styles.BoldText>
            <Styles.SmallText color="grey_3">
              {data.code ?? ''}
            </Styles.SmallText>
            <Styles.SmallText>
              {data.quantity ?? ''} {data.taxUnit ?? ''}
            </Styles.SmallText>
          </Box>
          <Box
            flex={1}
            width="100%"
            justifyContent="space-between"
            alignItems="center"
          >
            <Styles.SmallText color="grey_3">
              {data.itemInvoice?.invoiceCode
                ? `NF ${data.itemInvoice?.invoiceCode}`
                : ''}
            </Styles.SmallText>
            <Styles.Badge>{data.originItems?.length ?? 0} origens</Styles.Badge>
          </Box>
        </Box>
      </Styles.Node>

      {/* Handle para saída de conexão */}
      <Styles.CustomHandle
        type="source"
        style={{
          cursor: 'pointer',
          visibility: allowNextLevel ? 'visible' : 'hidden',
        }}
        position={direction === 'LR' ? Position.Right : Position.Bottom}
        onClick={() => actions.handleNodeClick(data)}
      />
    </>
  )
}
